/**
 * Steam 分版本分析 — 列表页入口
 * @description 页面入口组件，编排版本选择器、表格、图表、显示设置四大板块
 */
import React, { useState, useMemo, useRef } from 'react';
import { Layout } from 'tea-component';
import { connect } from 'umi';
import ContentBody from '@op/components/ContentBody';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import CardLoading from '@op/components/CardLoading';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_TM } from '@op/utils/format';
import { useVersionTitleList } from '@op/pages/SteamPerformance/hooks/useVersionTitleList';
import VersionCascadeFilter, {
  IVersionFilterState,
  cleanVersionFilter,
} from './components/VersionCascadeFilter';
import VersionAnalysisTable from './components/VersionAnalysisTable';
import VersionCompareChart from './components/VersionCompareChart';
import { PAGE_NAME_VERSION_ANALYSIS } from './constants';
import './index.scss';

const { Content } = Layout;

/** 默认版本筛选状态 */
const DEFAULT_VERSION_FILTER: IVersionFilterState = {
  update_titles: [],
  chapters: [],
  releases: [],
  patches: [],
  version_filters: [],
};

function VersionAnalysisList({
  gameInfo,
}: {
  gameInfo: any;
  wholeGameInfo: any;
}) {
  const scrollBody = useRef(null);
  const { formatMessage } = useLanguage();

  const {
    loading: initLoading,
    filter,
    changeFilter,
    filterOptions,
  } = useFilterDataHook({
    gameInfo,
    pageName: PAGE_NAME_VERSION_ANALYSIS,
  });

  // 版本筛选状态
  const [versionFilter, setVersionFilter] = useState<IVersionFilterState>(
    DEFAULT_VERSION_FILTER,
  );

  const handleVersionFilterChange = useMemo(
    () => (value: IVersionFilterState) => {
      setVersionFilter(value);
    },
    [],
  );

  // 获取版本标题列表和配置游戏判断
  const {
    titleList,
    cascadeOptions,
    isConfigGame,
    versionConfig,
  } = useVersionTitleList({
    gameInfo,
    filter,
  });

  const mergedFilter = useMemo(() => {
    if (!filter) return null;
    return {
      ...filter,
      ...cleanVersionFilter(versionFilter),
      is_dune: isConfigGame,
      channel: ['steam'],
      channel_type: 'comments',
    };
  }, [filter, versionFilter, isConfigGame]);

  const cumulativeFilter = useMemo(() => {
    if (!mergedFilter) return null;
    return {
      ...mergedFilter,
      trend_type: 'cumulative'
    };
  }, [mergedFilter]);

  return (
    <Content className="dbo-page dbo-page--version-analysis" ref={scrollBody}>
      <ContentBody
        contentHeard={
          <Content.Header
            title={formatMessage({ id: 'Version Analysis' })}
            className="dbo-header"
          />
        }
      >
        <FilterFixed
          filter={filterOptions}
          confirm={(value: any) => {
            const newFilter = { ...value, ...gameInfo };
            changeFilter(newFilter);
            if (
              newFilter.start_time !== filter?.start_time ||
              newFilter.end_time !== filter?.end_time ||
              newFilter.date_type !== filter?.date_type
            ) {
              setVersionFilter(DEFAULT_VERSION_FILTER);
            }
          }}
          defaultValue={{
            date_type: 'daily',
            ...DBO_TM.filterRangeDay(180, 1),
          }}
          defaultConfig={{
            date: { dateTypeList: ['date', 'week', 'month'] },
          }}
          scrollBody={scrollBody}
          operateShowOptions={['history', 'more', 'save']}
          isAccountRegion
        />
        {/* 板块 B：版本级联筛选 */}
        <VersionCascadeFilter
          titleList={titleList}
          cascadeOptions={cascadeOptions}
          isConfigGame={isConfigGame}
          versionConfig={versionConfig}
          value={versionFilter}
          onChange={handleVersionFilterChange}
        />
        <CardLoading loading={initLoading}>
          {/* 板块 E：版本列表表格（含显示设置 + 下载图标） */}
          <VersionAnalysisTable filter={mergedFilter} />

          {/* 板块 D：版本对比图表 */}
          <VersionCompareChart filter={mergedFilter} />

          {/* 板块 D：版本对比图表 */}
          <VersionCompareChart filter={cumulativeFilter} title={'Cumulative Trend'} />
        </CardLoading>
      </ContentBody>
    </Content>
  );
}

export default connect((state: any) => {
  const { opinionCommon, home } = state;

  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    userInfo: home.userInfo,
  };
})(VersionAnalysisList);
